import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import SearchMethodSelector from "./components/pan/SearchMethodSelector";
import FilterSection from "./components/pan/FilterSection";
import PanResultModal from "./components/pan/PanResultModal";
import ConfirmationModal from "./components/pan/ConfirmationModal";
import DataTable from "./components/pan/DataTable";
import Pagination from "./components/pan/Pagination";

/**
 * FindPan Component
 * Main page for finding PAN (Permanent Account Number) using Aadhaar or Phone number
 * Features:
 * - Search PAN using Aadhaar number (12 digits)
 * - Search PAN using Phone number (10 digits)
 * - Payment confirmation before search
 * - Search history with filtering and pagination
 * - Access control based on user permissions
 *
 * @returns {JSX.Element} FindPan page component
 */
export default function FindPan() {
  // ========================================
  // STATE MANAGEMENT - SEARCH INPUT
  // ========================================

  /**
   * Current search method selection
   * @type {string} 'aadhaar' or 'phone'
   */
  const [searchType, setSearchType] = useState("aadhaar");

  /**
   * Aadhaar number input value
   * Stores 12-digit Aadhaar number
   */
  const [aadhaarNumber, setAadhaarNumber] = useState("");

  /**
   * Phone number input value
   * Stores 10-digit phone number
   */
  const [phoneNumber, setPhoneNumber] = useState("");

  // ========================================
  // STATE MANAGEMENT - HISTORY & PAGINATION
  // ========================================

  /**
   * Complete history of all searches (merged API and local)
   * Used as source for filtering
   */
  const [allHistory, setAllHistory] = useState([]);

  /**
   * Filtered history based on current filters
   * Displayed in the data table
   */
  const [filteredHistory, setFilteredHistory] = useState([]);

  /**
   * Legacy history state (kept for compatibility)
   * Can be removed in future refactoring
   */
  const [history, setHistory] = useState([]);

  /**
   * Current page number for pagination
   * Starts from 1
   */
  const [currentPage, setCurrentPage] = useState(1);

  /**
   * Number of items to display per page
   * Fixed value for consistency
   */
  const itemsPerPage = 10;

  // ========================================
  // STATE MANAGEMENT - UI & MODALS
  // ========================================

  /**
   * Loading state during API calls
   * Used for button disabled state and spinner
   */
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Search result from API response
   * Contains PAN details and other info
   */
  const [searchResult, setSearchResult] = useState(null);

  /**
   * Show/hide confirmation modal
   * Displayed before payment and API call
   */
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  /**
   * Show/hide result modal
   * Displayed after successful search
   */
  const [showResultModal, setShowResultModal] = useState(false);

  // ========================================
  // STATE MANAGEMENT - ACCESS & PRICING
  // ========================================

  /**
   * Price for Aadhaar to PAN search
   * Retrieved from API, used in confirmation modal
   */
  const [aadhaarToPanPrice, setAadhaarToPanPrice] = useState(null);

  /**
   * Price for Phone to PAN search
   * Retrieved from API, used in confirmation modal
   */
  const [phoneToPanPrice, setPhoneToPanPrice] = useState(null);

  /**
   * Whether user has access to Aadhaar search
   * Controls input enable/disable state
   */
  const [aadhaarAccess, setAadhaarAccess] = useState(false);

  /**
   * Whether user has access to Phone search
   * Controls input enable/disable state
   */
  const [phoneAccess, setPhoneAccess] = useState(false);

  // ========================================
  // STATE MANAGEMENT - FILTERS
  // ========================================

  /**
   * Filter state for search history table
   * Includes: search term, status, date range
   */
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  // ========================================
  // API FUNCTIONS - PRICE & ACCESS
  // ========================================

  /**
   * Fetch pricing and access information from API
   * Determines if user can access Aadhaar/Phone search
   * Retrieves prices for each service
   *
   * @async
   * @returns {Promise<void>}
   */
  const fetchPriceData = async () => {
    console.log("=== Fetching Price Data ===");
    try {
      // Call API to get access and pricing info
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/access-check`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      console.log("Price Data Response:", response.data);
      console.log("Service Access Array:", response.data?.serviceAccess);
      console.log("Service Prices Object:", response.data?.servicePrices);

      // Extract access flags from response
      const aadhaarAccessValue =
        response.data?.serviceAccess.includes("Aadhar_pan");
      const phoneAccessValue =
        response.data?.serviceAccess.includes("Number_pan");

      // Extract prices from response and convert to numbers
      const aadhaarPrice =
        Number(response.data?.servicePrices?.Aadhar_pan) || 0;
      const phonePrice = Number(response.data?.servicePrices?.Number_pan) || 0;

      console.log("Aadhaar Access:", aadhaarAccessValue);
      console.log("Phone Access:", phoneAccessValue);
      console.log("Aadhar_pan Price:", aadhaarPrice);
      console.log("Number_pan Price:", phonePrice);

      // Update state with access and pricing info
      setAadhaarAccess(aadhaarAccessValue);
      setPhoneAccess(phoneAccessValue);
      setAadhaarToPanPrice(aadhaarPrice);
      setPhoneToPanPrice(phonePrice);

      console.log("=== Price Data Set Successfully ===");
    } catch (e) {
      // Log error details for debugging
      console.error("=== Error Fetching Price Data ===");
      console.error("Error:", e);
      console.error("Error Response:", e.response?.data);
    }
  };

  // ========================================
  // API FUNCTIONS - HISTORY FETCH
  // ========================================

  /**
   * Fetch search history from API
   * Merges API history with local storage history
   * Removes duplicates and sorts by date
   *
   * @async
   * @returns {Promise<void>}
   */
  const fetchData = async () => {
    console.log("=== Fetching History Data ===");
    try {
      setIsLoading(true);

      // Call API to fetch search records
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/get-document-records?service=findpan`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      console.log("History Data Response:", response.data);

      // Transform API response data to table format
      const historyData = response.data.map((item) => ({
        date: new Date(item.createdAt).toLocaleString(),
        searchType:
          item.documentType === "aadhaar-to-pan" ? "Aadhaar" : "Phone",
        searchValue: item.idNumber,
        panNumber: item.apiResponse?.panNumber || "N/A",
        details:
          item.documentType === "aadhaar-to-pan"
            ? "Aadhaar to PAN"
            : "Phone to PAN",
        status: item.status.charAt(0).toUpperCase() + item.status.slice(1),
        error: item.status === "failed" ? "Search failed" : "",
        createdAt: new Date(item.createdAt),
      }));

      // Get history from local storage and parse dates
      const localHistory = JSON.parse(
        localStorage.getItem("findPanHistory") || "[]",
      );
      const parsedLocalHistory = localHistory.map((item) => ({
        ...item,
        createdAt: new Date(item.createdAt),
      }));

      // Merge API and local history
      const mergedHistory = [...parsedLocalHistory, ...historyData];

      // Remove duplicate entries based on search value and date
      const uniqueHistory = mergedHistory.reduce((acc, current) => {
        const duplicate = acc.find(
          (item) =>
            item.searchValue === current.searchValue &&
            item.date === current.date,
        );
        if (!duplicate) {
          acc.push(current);
        }
        return acc;
      }, []);

      // Sort by date in descending order (newest first)
      uniqueHistory.sort((a, b) => b.createdAt - a.createdAt);

      console.log("Total History Records:", uniqueHistory.length);

      // Update all history states
      setAllHistory(uniqueHistory);
      setFilteredHistory(uniqueHistory);
      setHistory(uniqueHistory);

      // Persist to local storage for offline access
      localStorage.setItem("findPanHistory", JSON.stringify(uniqueHistory));

      console.log("=== History Data Set Successfully ===");
    } catch (error) {
      // Log error and show user notification
      console.error("=== Error Fetching History ===");
      console.error("Error:", error);
      console.error("Error Response:", error.response?.data);
      toast.error("Failed to fetch history");
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // EFFECTS
  // ========================================

  /**
   * Initial data fetch on component mount
   * Fetches pricing info and search history
   */
  useEffect(() => {
    console.log("=== Component Mounted - Initial Fetch ===");
    fetchData();
    fetchPriceData();
  }, []);

  /**
   * Apply filters whenever filters or history changes
   * Recalculates filtered data and resets pagination
   */
  useEffect(() => {
    applyFilters();
  }, [filters, allHistory]);

  // ========================================
  // VALIDATION & SUBMISSION
  // ========================================

  /**
   * Handle search submission
   * Validates input and checks access before showing confirmation
   * Shows validation errors via toast notifications
   */
  const handleSubmit = () => {
    console.log("=== Handle Submit ===");
    console.log("Search Type:", searchType);
    console.log("Aadhaar Number:", aadhaarNumber);
    console.log("Phone Number:", phoneNumber);
    console.log("Aadhaar Access:", aadhaarAccess);
    console.log("Phone Access:", phoneAccess);

    // ========== AADHAAR VALIDATION ==========
    if (searchType === "aadhaar") {
      // Check if input is empty
      if (!aadhaarNumber.trim()) {
        console.log("Validation Failed: Empty Aadhaar number");
        toast.error("Please enter Aadhaar number");
        return;
      }

      // Check if input length is correct (12 digits)
      if (aadhaarNumber.length !== 12) {
        console.log("Validation Failed: Aadhaar length", aadhaarNumber.length);
        toast.error("Aadhaar number must be 12 digits");
        return;
      }

      // Check if user has access to Aadhaar service
      if (!aadhaarAccess) {
        console.log("Validation Failed: No Aadhaar access");
        toast.error("You don't have access to Aadhaar to PAN service");
        return;
      }
    }
    // ========== PHONE VALIDATION ==========
    else {
      // Check if input is empty
      if (!phoneNumber.trim()) {
        console.log("Validation Failed: Empty phone number");
        toast.error("Please enter phone number");
        return;
      }

      // Check if input length is correct (10 digits)
      if (phoneNumber.length !== 10) {
        console.log("Validation Failed: Phone length", phoneNumber.length);
        toast.error("Phone number must be 10 digits");
        return;
      }

      // Check if user has access to Phone service
      if (!phoneAccess) {
        console.log("Validation Failed: No Phone access");
        toast.error("You don't have access to Phone to PAN service");
        return;
      }
    }

    // All validation passed, show confirmation modal
    console.log("Validation Passed - Opening Confirmation Modal");
    setShowConfirmationModal(true);
  };

  // ========================================
  // API CALL - SEARCH EXECUTION
  // ========================================

  /**
   * Execute PAN search API call after confirmation
   * Makes either Aadhaar-to-PAN or Phone-to-PAN API request
   * Adds result to history on success or failure
   *
   * @async
   * @returns {Promise<void>}
   */
  const proceedWithApiCall = async () => {
    console.log("=== Proceeding with API Call ===");
    console.log("Search Type:", searchType);
    console.log(
      "Current Price:",
      searchType === "aadhaar" ? aadhaarToPanPrice : phoneToPanPrice,
    );

    setIsLoading(true);
    setSearchResult(null);
    setShowConfirmationModal(false);

    try {
      let response;

      // ========== AADHAAR TO PAN API CALL ==========
      if (searchType === "aadhaar") {
        console.log("Making Aadhaar to PAN API call...");
        console.log("Request Payload:", { aadhaarNumber });

        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/services/pan/aadhaar-to-pan`,
          { aadhaarNumber: aadhaarNumber },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        console.log("Aadhaar to PAN Response:", response.data);
        console.log(`Aadhaar to PAN API Hit: ${response.data.apiUsed ? response.data.apiUsed.toUpperCase() : "UNKNOWN"}`);
      }
      // ========== PHONE TO PAN API CALL ==========
      else {
        console.log("Making Phone to PAN API call...");
        console.log("Request Payload:", {
          mobile_no: phoneNumber,
          first_name: firstName,
          last_name: lastName,
        });

        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/services/pan/phone-number-to-pan`,
          {
            mobile_no: phoneNumber,
            first_name: firstName,
            last_name: lastName,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        console.log("Phone to PAN Response:", response.data);
        console.log(`Phone to PAN API Hit: ${response.data.apiUsed ? response.data.apiUsed.toUpperCase() : "UNKNOWN"}`);
      }

      // Check if API returned success status
      if (response.data.success === false) {
        console.log("API returned success: false");
        throw new Error(response.data.message || "Failed to find PAN");
      }

      console.log("API Call Successful");
      console.log("Search Result Data:", response.data.data);

      // Store result and show modal
      setSearchResult(response.data.data);
      setShowResultModal(true);
      setPhoneNumber("");
      setFirstName("");
      setLastName("");

      // ========== ADD TO HISTORY (SUCCESS) ==========
      const newEntry = {
        date: new Date().toLocaleString(),
        searchType: searchType === "aadhaar" ? "Aadhaar" : "Phone",
        searchValue: searchType === "aadhaar" ? aadhaarNumber : phoneNumber,
        panNumber:
          response.data.data.panNumber ||
          response.data.data.pan_number ||
          "N/A",
        details: searchType === "aadhaar" ? "Aadhaar to PAN" : "Phone to PAN",
        status: "Success",
        createdAt: new Date(),
      };

      console.log("New History Entry:", newEntry);

      // Update all history states with new entry
      const updatedHistory = [newEntry, ...allHistory];
      setAllHistory(updatedHistory);
      setFilteredHistory([newEntry, ...filteredHistory]);
      setHistory([newEntry, ...history]);

      // Clear input fields
      setAadhaarNumber("");
      setPhoneNumber("");

      // Show success notification
      toast.success("PAN found successfully!");
      console.log("=== API Call Completed Successfully ===");
    } catch (err) {
      // ========== ERROR HANDLING ==========
      console.error("=== Error in API Call ===");
      console.error("Error:", err);
      console.error("Error Response:", err.response?.data);
      console.error("Error Status:", err.response?.status);

      // Construct error message
      let errorMessage = "Failed to find PAN. Please try again.";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      console.log("Error Message to Display:", errorMessage);
      toast.error(errorMessage);

      // ========== ADD TO HISTORY (FAILURE) ==========
      const newEntry = {
        date: new Date().toLocaleString(),
        searchType: searchType === "aadhaar" ? "Aadhaar" : "Phone",
        searchValue: searchType === "aadhaar" ? aadhaarNumber : phoneNumber,
        panNumber: "N/A",
        status: "Failed",
        error: errorMessage,
        createdAt: new Date(),
      };

      console.log("Failed History Entry:", newEntry);

      // Update all history states with failed entry
      const updatedHistory = [newEntry, ...allHistory];
      setAllHistory(updatedHistory);
      setFilteredHistory([newEntry, ...filteredHistory]);
      setHistory([newEntry, ...history]);

      console.log("=== API Call Failed ===");
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // FILTERING & PAGINATION
  // ========================================

  /**
   * Apply filters to history data
   * Filters by search term, status, and date range
   * Resets pagination to page 1
   */
  const applyFilters = () => {
    console.log("=== Applying Filters ===");
    console.log("Current Filters:", filters);

    let filteredData = [...allHistory];

    // ========== SEARCH TERM FILTER ==========
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      console.log("Search Term:", searchTerm);
      filteredData = filteredData.filter(
        (item) =>
          item.searchValue.toLowerCase().includes(searchTerm) ||
          (item.panNumber && item.panNumber.toLowerCase().includes(searchTerm)),
      );
    }

    // ========== STATUS FILTER ==========
    if (filters.status) {
      console.log("Status Filter:", filters.status);
      filteredData = filteredData.filter(
        (item) => item.status.toLowerCase() === filters.status.toLowerCase(),
      );
    }

    // ========== DATE RANGE FILTER ==========
    if (filters.fromDate || filters.toDate) {
      const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
      const toDate = filters.toDate ? new Date(filters.toDate) : null;

      console.log("Date Range Filter:", { fromDate, toDate });

      // Set time boundaries for date comparison
      if (fromDate) fromDate.setHours(0, 0, 0, 0);
      if (toDate) toDate.setHours(23, 59, 59, 999);

      filteredData = filteredData.filter((item) => {
        const itemDate = new Date(item.createdAt);
        let matches = true;

        if (fromDate && itemDate < fromDate) matches = false;
        if (toDate && itemDate > toDate) matches = false;

        return matches;
      });
    }

    console.log("Filtered Results Count:", filteredData.length);
    setFilteredHistory(filteredData);
    setCurrentPage(1);
  };

  /**
   * Clear all active filters
   * Resets filter state to initial values
   */
  const clearFilters = () => {
    console.log("=== Clearing Filters ===");
    setFilters({ search: "", status: "", fromDate: "", toDate: "" });
    setFilteredHistory(allHistory);
    setCurrentPage(1);
  };

  // ========================================
  // HELPER FUNCTIONS
  // ========================================

  /**
   * Get current price based on selected search type
   * Used in confirmation modal to show the charge
   *
   * @returns {number} Current price for the selected search type
   */
  const getCurrentPrice = () => {
    const price =
      searchType === "aadhaar" ? aadhaarToPanPrice : phoneToPanPrice;
    console.log("Current Price for", searchType, ":", price);
    return price;
  };

  /**
   * Calculate pagination values
   * Determine total pages and current data slice
   */
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const currentData = filteredHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // ========================================
  // TABLE CONFIGURATION
  // ========================================

  /**
   * Data table column definitions
   * Specifies which fields to display and how to render them
   */
  const tableColumns = [
    {
      header: "Sl No",
      render: (item, idx) => idx + 1,
    },
    {
      header: "Date & Time",
      field: "date",
    },
    {
      header: "Search Type",
      field: "searchType",
    },
    {
      header: "Search Value",
      render: (item) => <span className="font-mono">{item.searchValue}</span>,
    },
    {
      header: "PAN Number",
      render: (item) => (
        <span className="font-mono font-bold text-blue-700">
          {item.panNumber}
        </span>
      ),
    },
    {
      header: "Status",
      render: (item) => (
        <span
          className={`px-2 py-1 rounded text-white text-xs ${
            item.status === "Success" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          {item.status}
        </span>
      ),
    },
  ];

  // ========================================
  // RENDER
  // ========================================

  return (
    <div className="py-4 w-full px-2 sm:px-4">
      {/* Header */}
      <div className="bg-blue-100 p-2 mb-4 rounded-md">
        <h2 className="text-center text-lg sm:text-xl font-semibold text-blue-700">
          Find PAN Number
        </h2>
      </div>

      <div className="space-y-4">
        {/* Search Section */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            Search PAN Number
          </h3>

          <div className="space-y-4">
            <SearchMethodSelector
              searchType={searchType}
              onSearchTypeChange={(type) => {
                setSearchType(type);
                setFirstName("");
                setLastName("");
              }}
              aadhaarAccess={aadhaarAccess}
              phoneAccess={phoneAccess}
            />

            <div>
              {/* Label: only show for Aadhaar (single field) */}
              {searchType === "aadhaar" && (
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Aadhaar Number
                </label>
              )}

              {/* Responsive row container */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                {/* Inputs group - flex grow */}
                <div className="flex-1 w-full">
                  {searchType === "aadhaar" ? (
                    <input
                      type="text"
                      placeholder="Enter 12-digit Aadhaar Number"
                      value={aadhaarNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setAadhaarNumber(value);
                      }}
                      disabled={!aadhaarAccess}
                      className={`w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        !aadhaarAccess
                          ? "bg-gray-100 cursor-not-allowed"
                          : "bg-white"
                      }`}
                      maxLength={12}
                    />
                  ) : (
                    /* Phone: three labelled inputs (no outer label) */
                    <div className="flex flex-wrap gap-4">
                      {/* Phone Number */}
                      <div className="flex-1 min-w-[120px]">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Enter 10-digit number"
                          value={phoneNumber}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setPhoneNumber(value);
                          }}
                          disabled={!phoneAccess}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          maxLength={10}
                        />
                      </div>

                      {/* First Name (optional) */}
                      <div className="flex-1 min-w-[120px]">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          First Name{" "}
                          <span className="text-gray-400 text-xs">
                            (optional)
                          </span>
                        </label>
                        <input
                          type="text"
                          placeholder="First name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          disabled={!phoneAccess}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Last Name (optional) */}
                      <div className="flex-1 min-w-[120px]">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Last Name{" "}
                          <span className="text-gray-400 text-xs">
                            (optional)
                          </span>
                        </label>
                        <input
                          type="text"
                          placeholder="Last name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          disabled={!phoneAccess}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Search Button */}
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className={`w-full sm:w-auto px-8 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors font-medium ${
                    isLoading ||
                    (searchType === "aadhaar"
                      ? !aadhaarNumber || !aadhaarAccess
                      : !phoneNumber || !phoneAccess)
                      ? "opacity-70 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Searching...
                    </span>
                  ) : (
                    "SEARCH PAN"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* History Section */}
        <div className="bg-white p-4 shadow-md rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Search History</h3>
          <FilterSection
            filters={filters}
            onFilterChange={setFilters}
            onApply={applyFilters}
            onClear={clearFilters}
          />
          <DataTable
            data={currentData}
            columns={tableColumns}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredHistory.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {/* Modals */}
      <ConfirmationModal
        show={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={proceedWithApiCall}
        title="Confirm Payment"
        message={`This ${searchType === "aadhaar" ? "Aadhaar" : "Phone"} to PAN search will be charged. Do you want to proceed?`}
        price={getCurrentPrice()}
      />
      <PanResultModal
        show={showResultModal}
        onClose={() => {
          setShowResultModal(false);
          setSearchResult(null);
        }}
        searchResult={searchResult}
        searchType={searchType}
      />
    </div>
  );
}
